import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const LAST_PATH_KEY = "rc:lastPath";
const SCROLL_KEY = "rc:scroll:";

/** The last portal page the user was on, so reopening the app lands there instead of on the Dashboard. */
export function rememberedPath(): string | null {
  try {
    const value = localStorage.getItem(LAST_PATH_KEY);
    return value && /^\/(app|admin)(\/|\?|$)/.test(value) ? value : null;
  } catch {
    return null;
  }
}

export function forgetRememberedPath(): void {
  try {
    localStorage.removeItem(LAST_PATH_KEY);
  } catch {
    // nothing to forget
  }
}

/**
 * Keeps the user's place when the app comes back from the background or the phone had thrown the page away:
 *  - remembers the current page (rememberedPath, used when the app is opened at "/");
 *  - remembers how far down each page you were, and on a fresh load scrolls back there once the page's
 *    content has arrived. Moving between pages inside the app is left alone.
 * Renders nothing. Mount it once, inside the router.
 */
export function PageMemory() {
  const { pathname, search } = useLocation();
  const restored = useRef(false);

  // Remember where we are.
  useEffect(() => {
    if (!/^\/(app|admin)(\/|$)/.test(pathname)) return;
    try {
      localStorage.setItem(LAST_PATH_KEY, pathname + search);
    } catch {
      // not persisted
    }
  }, [pathname, search]);

  // Remember how far down this page is.
  useEffect(() => {
    let timer: number | undefined;
    const save = () => {
      try {
        sessionStorage.setItem(SCROLL_KEY + pathname, String(Math.round(window.scrollY)));
      } catch {
        // not persisted
      }
    };
    const onScroll = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(save, 150);
    };
    const onHide = () => document.visibilityState === "hidden" && save();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", save);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", save);
      document.removeEventListener("visibilitychange", onHide);
      save(); // leaving this page: keep where it was
    };
  }, [pathname]);

  // On a fresh load, go back to where this page was scrolled once it is tall enough to get there.
  useEffect(() => {
    if (restored.current || pathname === "/" || pathname === "/login") return;
    restored.current = true;
    let target = 0;
    try {
      target = Number(sessionStorage.getItem(SCROLL_KEY + pathname)) || 0;
    } catch {
      return;
    }
    if (target <= 0) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries++;
      const reachable = document.documentElement.scrollHeight - window.innerHeight >= target;
      if (window.scrollY > 5 || tries > 40) {
        window.clearInterval(timer); // the user has already scrolled, or the page never got tall enough
      } else if (reachable) {
        window.scrollTo(0, target);
        window.clearInterval(timer);
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [pathname]);

  return null;
}
